import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Mail } from 'lucide-react';
import { JESSICA_PHOTO, PHONE_NUMBER, PHONE_TEL_HREF, EMAIL_ADDRESS, ABOUT_ME_PARAGRAPHS, ABOUT_ME_CLOSER } from '../../constants/site';
import { LANDING_PAGES } from '../../constants/landingPages';

const CALL_LABEL = `Call or Text Jess: ${PHONE_NUMBER}`;

export const Eyebrow = ({ children, light }) => (
  <p className={`text-xs font-bold tracking-[0.3em] uppercase ${light ? 'text-gold-300' : 'text-gold-600'}`}>{children}</p>
);

export const CallButton = ({ testId, className = '' }) => (
  <a href={PHONE_TEL_HREF} data-testid={testId} className={`inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-[#143160] font-black px-7 py-4 rounded-md text-base transition-colors ${className}`}>
    <Phone className="w-5 h-5 mr-2" />{CALL_LABEL}
  </a>
);

export const LandingHero = ({ page }) => (
  <section className={`relative overflow-hidden bg-[#143160] text-white pt-32 sm:pt-40 ${page.heroImage ? 'pb-24 sm:pb-32 min-h-[34rem]' : 'pb-16 sm:pb-20'}`} data-testid="landing-hero">
    {page.heroImage ? (
      <>
        <img src={page.heroImage} alt={page.heroAlt || page.h1} className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" decoding="async" data-testid="landing-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#143160] via-[#143160]/85 to-[#143160]/30"></div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#143160]/70 to-transparent"></div>
      </>
    ) : (
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,#BFA26A,transparent_45%)]"></div>
    )}
    <div className="relative max-w-4xl mx-auto px-5 space-y-6">
      <Eyebrow light>{page.eyebrow}</Eyebrow>
      <h1 data-testid="landing-h1" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] drop-shadow-lg">{page.h1}</h1>
      <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl drop-shadow">{page.intro}</p>
      <div className="h-px w-40 bg-gold-500"></div>
      <CallButton testId="landing-hero-call" />
    </div>
    {page.heroCredit && <p className="absolute bottom-3 right-4 text-[11px] text-white/60" data-testid="landing-hero-credit">Photo: {page.heroCredit}</p>}
  </section>
);

const scrollToForm = () => document.getElementById('home-value-form')?.scrollIntoView({ behavior: 'smooth' });

export const LandingClosing = ({ closing }) => (
  <section className="bg-sand-50 border-y border-[#143160]/10 py-16 sm:py-20 text-center px-5" data-testid="landing-closing">
    <div className="max-w-2xl mx-auto space-y-5">
      <Eyebrow>{closing.eyebrow}</Eyebrow>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#143160]">{closing.h2}</h2>
      <p className="text-lg text-neutral-700 leading-relaxed">{closing.text}</p>
      {closing.cta === 'valuation' ? (
        <button type="button" onClick={scrollToForm} data-testid="landing-closing-valuation" className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-[#143160] font-black px-7 py-4 rounded-md text-base transition-colors">
          Get My Free Home Value <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      ) : (
        <CallButton testId="landing-closing-call" />
      )}
    </div>
  </section>
);

export const JessBio = () => (
  <section className="max-w-5xl mx-auto px-5 py-16" data-testid="landing-bio">
    <div className="bg-[#143160] text-white rounded-3xl p-8 sm:p-10 grid sm:grid-cols-[auto_1fr] gap-8 items-center">
      <img src={JESSICA_PHOTO} alt="Jessica Burnette, REALTOR®" className="w-32 h-32 rounded-full object-cover object-top border-4 border-gold-500 mx-auto" loading="lazy" decoding="async" />
      <div className="space-y-4 text-center sm:text-left">
        <Eyebrow light>Your Coastal REALTOR®</Eyebrow>
        <h2 className="font-serif text-3xl">Hi, I'm Jess.</h2>
        <div className="space-y-3 text-white/85 leading-relaxed" data-testid="landing-bio-copy">
          {ABOUT_ME_PARAGRAPHS.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          <p className="font-bold text-gold-300">{ABOUT_ME_CLOSER}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center sm:justify-start">
          <a href={PHONE_TEL_HREF} className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-[#143160] font-black px-6 py-3 rounded-md transition-colors"><Phone className="w-4 h-4 mr-2" />{PHONE_NUMBER}</a>
          <a href={`mailto:${EMAIL_ADDRESS}`} className="inline-flex items-center justify-center border-2 border-gold-500 text-gold-300 hover:bg-gold-500 hover:text-[#143160] font-bold px-6 py-3 rounded-md transition-colors"><Mail className="w-4 h-4 mr-2" />Email Jess</a>
        </div>
      </div>
    </div>
  </section>
);

export const MoreGuides = ({ currentSlug }) => {
  const others = useMemo(() => LANDING_PAGES.filter((pg) => pg.slug !== currentSlug), [currentSlug]);
  return (
    <section className="bg-ivory border-t border-[#143160]/10 py-12">
      <div className="max-w-5xl mx-auto px-5">
        <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold mb-4">More Guides &amp; Neighborhoods</p>
        <div className="flex flex-wrap gap-2">
          {others.map((pg) => (
            <Link key={pg.slug} to={`/${pg.slug}`} data-testid={`landing-link-${pg.slug}`} className="px-4 py-2 rounded-full border border-[#143160]/20 text-sm text-[#143160] hover:bg-[#143160] hover:text-white transition-colors">{pg.footerLabel}</Link>
          ))}
          <Link to="/home-value" data-testid="landing-link-home-value" className="px-4 py-2 rounded-full border border-[#143160]/20 text-sm text-[#143160] hover:bg-[#143160] hover:text-white transition-colors">Free Home Valuation</Link>
        </div>
      </div>
    </section>
  );
};
